import React, { useState, useEffect } from 'react'

interface Testimonial {
  id: string
  location: string
  rating: number
  testimonial: string
}

const TestimonialToaster: React.FC = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState<Testimonial | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isPageVisible, setIsPageVisible] = useState(true)

  // Universal Pay testimonials data
  const testimonials: Testimonial[] = [
    {
      id: '1',
      location: 'New York, USA',
      rating: 5,
      testimonial: 'We managed to integrate the wire transfers feature, leading to seamless transactions.'
    },
    {
      id: '2',
      location: 'London, UK',
      rating: 5,
      testimonial: 'We managed to setup the crypto support feature, leading to global reach.'
    },
    {
      id: '3',
      location: 'Tokyo, Japan',
      rating: 4,
      testimonial: 'We managed to deploy the card payments feature, leading to customer satisfaction.'
    },
    {
      id: '4',
      location: 'Berlin, Germany',
      rating: 5,
      testimonial: 'We managed to customize the eVoucher solutions feature, leading to flexibility.'
    },
    {
      id: '5',
      location: 'Sydney, Australia',
      rating: 5,
      testimonial: 'We managed to launch the payment API feature, leading to speed.'
    },
    {
      id: '6',
      location: 'Toronto, Canada',
      rating: 4,
      testimonial: 'We managed to connect the merchant dashboard feature, leading to ease of use.'
    },
    {
      id: '7',
      location: 'Paris, France',
      rating: 5,
      testimonial: 'We managed to enable the mobile payments feature, leading to secure payments.'
    },
    {
      id: '8',
      location: 'Amsterdam, Netherlands',
      rating: 5,
      testimonial: 'We managed to integrate the multi-currency support, leading to global reach.'
    },
    {
      id: '9',
      location: 'Singapore',
      rating: 4,
      testimonial: 'We managed to setup the fraud protection feature, leading to security.'
    },
    {
      id: '10',
      location: 'Dubai, UAE',
      rating: 5,
      testimonial: 'We managed to deploy the real-time analytics, leading to better insights.'
    }
  ]

  const getRandomTestimonial = () => {
    const randomIndex = Math.floor(Math.random() * testimonials.length)
    return testimonials[randomIndex]
  }

  const showTestimonial = () => {
    // Only show if page is visible and no testimonial is currently showing
    if (!isPageVisible || isVisible) return

    const testimonial = getRandomTestimonial()
    setCurrentTestimonial(testimonial)
    setIsVisible(true)

    // Hide after 4 seconds
    setTimeout(() => {
      setIsVisible(false)
    }, 4000)

    // Clear testimonial after animation
    setTimeout(() => {
      setCurrentTestimonial(null)
    }, 4500)
  }

  // Get random interval between 5-30 seconds
  const getRandomInterval = () => {
    return Math.random() * 25000 + 5000 // 5000ms to 30000ms
  }

  useEffect(() => {
    // Handle page visibility changes
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    let initialTimer: number
    let intervalTimer: number

    const scheduleNextTestimonial = () => {
      const delay = getRandomInterval()
      intervalTimer = setTimeout(() => {
        showTestimonial()
        scheduleNextTestimonial() // Schedule the next one
      }, delay)
    }

    // Show first testimonial after 3 seconds if page is visible
    if (isPageVisible) {
      initialTimer = setTimeout(() => {
        showTestimonial()
        scheduleNextTestimonial() // Start the random scheduling
      }, 3000)
    }

    return () => {
      clearTimeout(initialTimer)
      clearTimeout(intervalTimer)
    }
  }, [isPageVisible])

  if (!currentTestimonial) return null

  return (
    <div className={`fixed top-20 right-4 z-50 transition-all duration-500 transform ${
      isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
    }`}>
      <div className="glass-card-intense p-4 rounded-xl max-w-sm shadow-2xl border border-brand-blue/20">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-r from-brand-blue to-brand-purple rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">
                {currentTestimonial.location.split(',')[0].charAt(0)}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <p className="text-sm font-medium text-white truncate">
                {currentTestimonial.location}
              </p>
              <div className="flex">
                {[...Array(currentTestimonial.rating)].map((_, i) => (
                  <svg key={i} className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-300 leading-relaxed">
              "{currentTestimonial.testimonial}"
            </p>
          </div>
        </div>
        
        {/* Close button */}
        <button 
          onClick={() => setIsVisible(false)}
          className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default TestimonialToaster
